/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import type { TablePaginationConfig, TableProps } from "antd"
import { Table, Tag, Space, Typography } from "antd"
import type React from "react"

const { Text } = Typography
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"

interface CollocationTableProps {
  data: any[]
  loading: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void
  pagination?: TableProps["pagination"]
}

const getCollocationTypeColor = (type: string) => {
  const normalized = (type || "").trim().toLowerCase()
  switch (normalized) {
    case "verb":
      return "gold"
    case "noun":
      return "blue"
    case "adj":
    case "adjective":
      return "green"
    case "adv":
    case "adverb":
      return "purple"
    default:
      return "default"
  }
}

const CollocationTable: React.FC<CollocationTableProps> = ({
  data,
  loading,
  selectedRowKeys,
  onSelectionChange,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 80,
      align: "center",
      render: (value: number) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Collocation",
      dataIndex: "phrase",
      key: "phrase",
      width: 250,
      render: (value: string, record: any) => (
        <Space direction="vertical" size={2}>
          <span className="font-bold text-primary">{value}</span>
          {record.type && (
            <Tag color={getCollocationTypeColor(record.type)} className="text-xs">
              {record.type.toUpperCase()}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Ý nghĩa & Định nghĩa",
      key: "meaning_definition",
      render: (_: any, record: any) => {
        if (!record.meaning && !record.definition) return <Text type="secondary">—</Text>
        return (
          <div className="text-xs">
            {record.meaning && (
              <div>
                <Text strong>{record.meaning}</Text>
              </div>
            )}
            {record.definition && (
              <div className="mt-1">
                <Text type="secondary" italic>
                  {record.definition}
                </Text>
              </div>
            )}
          </div>
        )
      },
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

CollocationTable.displayName = "CollocationTable"

export default CollocationTable
